import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Star } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, MaskedLines } from "../components/Motion";
import Marquee from "../components/Marquee";
import Newsletter from "../components/Newsletter";
import LiveProductsSection from "../components/LiveProductsSection";
import Seo from "../components/Seo";
import FaqSection from "../components/FaqSection";
import { IMAGES } from "../lib/images";
import { getArticles } from "../lib/api";

const easeLux = [0.22, 1, 0.36, 1];

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="MYSYMP AG" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-obsidian/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian/70 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ y, opacity }} className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-6 md:px-12">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 text-xs uppercase tracking-[0.3em] text-champagne"
        >
          {t.hero.overline}
        </motion.span>

        <h1 className="font-serif text-6xl font-light leading-[0.92] tracking-tight text-white sm:text-7xl md:text-8xl lg:text-[9rem]">
          <MaskedLines lines={[t.hero.l1, t.hero.l2]} startDelay={0.25} />
          <span className="block overflow-hidden">
            <motion.span
              className="block italic text-champagne"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.55, ease: easeLux }}
            >
              {t.hero.l3}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: easeLux }}
          className="mt-10 max-w-xl text-base font-light leading-relaxed text-white/70 md:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease: easeLux }}
          className="mt-12 flex flex-col gap-4 sm:flex-row"
        >
          <a
            href="#universe"
            data-testid="hero-cta-primary"
            className="group flex items-center justify-center gap-3 bg-champagne px-8 py-4 text-xs uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-champagne-light"
          >
            {t.hero.cta}
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <Link
            to="/contact"
            data-testid="hero-cta-secondary"
            className="flex items-center justify-center gap-3 border border-white/25 px-8 py-4 text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-champagne hover:text-champagne"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};

const Manifesto = () => {
  const { t } = useLang();
  return (
    <section className="relative bg-obsidian py-28 md:py-44" data-testid="manifesto">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.manifesto.overline}</span>
          <h2 className="mt-6 max-w-2xl font-serif text-4xl font-light leading-tight md:text-6xl">{t.manifesto.title}</h2>
        </Reveal>

        <div className="mt-24 space-y-28 md:space-y-40">
          {t.manifesto.chapters.map((c, i) => (
            <div
              key={i}
              className={`relative grid items-center gap-8 md:grid-cols-12 ${i % 2 === 1 ? "md:text-right" : ""}`}
            >
              <div className={`relative md:col-span-7 ${i % 2 === 1 ? "md:col-start-6 md:text-left" : ""}`}>
                <span className="pointer-events-none absolute -top-24 -left-4 select-none font-serif text-[10rem] italic leading-none text-champagne/10 md:-top-32 md:text-[16rem]">
                  0{i + 1}
                </span>
                <Reveal delay={0.05}>
                  <h3 className="relative font-serif text-3xl font-light leading-tight text-white md:text-5xl">{c.t}</h3>
                </Reveal>
                <Reveal delay={0.15}>
                  <p className="relative mt-6 max-w-lg text-base font-light leading-relaxed text-white/55 md:text-lg">{c.d}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const showcaseItems = (t) => [
  { key: "consulting", to: "/consulting", image: IMAGES.consulting, span: "md:col-span-7 md:row-span-2", h: "h-[520px] md:h-full", featured: true },
  { key: "interior", to: "/interior", image: IMAGES.interior, span: "md:col-span-5", h: "h-[420px]" },
  { key: "fashion", to: "/fashion", image: IMAGES.fashion, span: "md:col-span-5", h: "h-[420px]" },
  { key: "wine", to: "/wine", image: IMAGES.wine, span: "md:col-span-4", h: "h-[420px]" },
  { key: "realEstate", to: "/real-estate", image: IMAGES.realEstate, span: "md:col-span-4", h: "h-[420px]" },
  { key: "library", to: "/library", image: IMAGES.library, span: "md:col-span-4", h: "h-[420px]" },
];

const ShowcaseCard = ({ item, t }) => {
  const area = t.areas[item.key];
  return (
    <Link
      to={item.to}
      data-testid={`showcase-card-${item.key}`}
      className={`group relative block overflow-hidden border ${item.featured ? "border-champagne/40" : "border-white/10"} ${item.span} ${item.h}`}
    >
      <img
        src={item.image}
        alt={area.name}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian/95 via-obsidian/30 to-transparent transition-opacity duration-500 group-hover:from-obsidian/90" />
      {item.featured && (
        <span className="absolute left-8 top-8 z-10 flex items-center gap-2 border border-champagne bg-obsidian/40 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-champagne backdrop-blur-sm">
          <Star size={11} className="fill-champagne" /> {t.showcase.featured}
        </span>
      )}
      <div className="relative flex h-full flex-col justify-end p-8">
        <span className="text-[10px] uppercase tracking-[0.3em] text-champagne">{area.tag}</span>
        <h3 className={`mt-3 font-serif font-light text-white ${item.featured ? "text-4xl md:text-6xl" : "text-3xl md:text-4xl"}`}>{area.name}</h3>
        <p className={`mt-3 font-light leading-relaxed text-white/60 transition-all duration-500 ${item.featured ? "max-w-md text-base opacity-100" : "max-w-sm text-sm opacity-0 group-hover:opacity-100"}`}>
          {area.desc}
        </p>
        <span className="mt-5 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80">
          {t.showcase.cta}
          <ArrowRight size={14} className="text-champagne transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const Showcase = () => {
  const { t } = useLang();
  return (
    <section id="universe" className="scroll-mt-24 border-t border-white/10 bg-obsidian-soft py-28 md:py-40" data-testid="showcase">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.showcase.overline}</span>
            <h2 className="mt-5 max-w-xl font-serif text-4xl font-light leading-tight md:text-6xl">{t.showcase.title}</h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="grid auto-rows-[420px] grid-cols-1 gap-5 md:grid-cols-12">
            {showcaseItems(t).map((item) => (
              <ShowcaseCard key={item.key} item={item} t={t} />
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FeaturedArticles = () => {
  const { t, lang } = useLang();
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    getArticles("knowledge").then(setArticles).catch(() => {});
  }, []);
  if (!articles.length) return null;
  return (
    <section className="border-t border-white/10 bg-obsidian py-28 md:py-40" data-testid="featured-articles">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="mb-16 flex items-end justify-between gap-6">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-champagne">{t.library.overline}</span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight md:text-6xl">{t.library.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/library" className="hidden items-center gap-2 text-xs uppercase tracking-[0.2em] text-champagne link-sweep md:flex">
              {t.library.more} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-8 md:grid-cols-3">
          {articles.slice(0, 3).map((a, i) => (
            <Reveal key={a.id} delay={i * 0.1}>
              <Link to={`/library/${a.slug}`} data-testid={`featured-article-${a.slug}`} className="group block">
                <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
                  <img src={a.cover_image} alt={a.title[lang]} className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <span className="mt-5 block text-[10px] uppercase tracking-[0.25em] text-champagne">{a.read_time} {t.library.readTime}</span>
                <h3 className="mt-3 font-serif text-2xl font-light leading-snug text-white transition-colors group-hover:text-champagne">{a.title[lang]}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-white/50">{a.excerpt[lang]}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div data-testid="home-page">
      <Seo pageKey="home" />
      <Hero />
      <Marquee />
      <Manifesto />
      <Showcase />
      <LiveProductsSection />
      <FeaturedArticles />
      <FaqSection pageKey="home" />
      <Newsletter />
    </div>
  );
};

export default Home;
